import { Controller } from '@hotwired/stimulus'
import React from 'react'
import { createRoot } from 'react-dom/client'
import CoachChat from '../components/coach_chat/coach/CoachChat'

export default class extends Controller {
  static targets = ['container']
  static values = {
    // Socket and room configuration
    socketUrl: String,
    username: String,
    chatRoom: String,
    logChatEventPath: String,
    serverTime: Number,
    currentUserLocale: String,

    // Coach-specific information
    userDisplayName: String,
    userFirstName: String,
    userAbbreviatedName: String,
    avatar: String,
    bigAvatar: String,
    isTechSupport: Boolean,
    browserName: String,
    os: String,
    coachLocales: Array,

    // Chat configuration and paths
    coachChatPath: String,
    logChatMissedOpportunityPath: String,
    checkCoachHeartbeatPath: String,
    coachChatCandidateInfoPath: String,
    endChatPath: String,
    autoconnect: Boolean,

    // Availability configuration
    initialAvailability: { type: String, default: 'away' },

    // Coach numeric id for room coachId / candidate coach-info API
    userId: Number,

    // Audio settings URLs as arrays
    ringToneOptions: Array,
    messageSoundOptions: Array,
  }

  connect() {
    console.log('[STIMULUS_DEBUG] CoachChatController connected', this.element)
    // Check availability from URL and set header links to open in new tab if available
    this.updateHeaderLinksForAvailability()
    // Listen for availability changes from React component
    this.availabilityChangeHandler = (event) => {
      this.handleAvailabilityChange(event.detail.availability)
    }
    window.addEventListener(
      'coachAvailabilityChanged',
      this.availabilityChangeHandler,
    )
    this.setupLogoutHandlers()
    // Wait for easyrtc to be available before initializing React
    this.waitForEasyRTC()
  }

  disconnect() {
    console.log(
      '[STIMULUS_DEBUG] CoachChatController disconnected',
      this.element,
    )
    // Clean up React root if it exists
    if (this.reactRoot) {
      this.reactRoot.unmount()
    }
    // Remove event listener
    if (this.availabilityChangeHandler) {
      window.removeEventListener(
        'coachAvailabilityChanged',
        this.availabilityChangeHandler,
      )
    }
    this.teardownLogoutHandlers()
    // Restore header links to their original state
    this.restoreHeaderLinks()
  }

  isLogoutLink(link) {
    const href = link.getAttribute('href') || ''
    return href.includes('/logout')
  }

  logoutLinkContainers() {
    return [
      document.querySelector('body > .header'),
      document.querySelector('body > nav.navbar'),
    ].filter(Boolean)
  }

  setupLogoutHandlers() {
    this.logoutLinks = new Map()
    this.logoutReadyHandler = (event) => {
      this.navigateToLogout(event.detail?.logoutUrl)
    }

    this.logoutLinkContainers().forEach((container) => {
      container.querySelectorAll('a').forEach((link) => {
        if (!this.isLogoutLink(link)) {
          return
        }

        link.removeAttribute('target')

        const handler = (event) => {
          event.preventDefault()
          event.stopPropagation()
          this.confirmLogout(link)
        }

        link.addEventListener('click', handler, true)
        this.logoutLinks.set(link, handler)
      })
    })
  }

  confirmLogout(link) {
    const $ = window.jQuery || window.$
    if (!$?.rails?.allowAction) {
      // init.js may not be ready yet; fall back so logout still works
      if (
        !window.confirm(
          'Are you sure you want to log out? You will be marked as unavailable and your chat session will end.',
        )
      ) {
        return
      }
      this.startLogout(link.getAttribute('href') || '/logout')
      return
    }

    // allowAction reuses #confirm_modal when present; ensure it is in the DOM
    // so the proceed button can be intercepted after the modal opens.
    if (!$('#confirm_modal').length) {
      $(
        '<div id="confirm_modal" class="modal fade confirm-modal" role="dialog" aria-modal="true" aria-labelledby="confirm_modal_title" aria-describedby="confirm_modal_description"></div>',
      ).appendTo(document.body)
    }

    $.rails.allowAction($(link))

    const $modal = $('#confirm_modal')
    // allowAction clones the link as the proceed control; intercept so we can
    // run coach availability cleanup before navigating away.
    const $proceed = $modal.find('.modal-footer .btn-danger')
    $proceed.off('click')
    $proceed.removeAttr('data-method')
    $proceed.on('click', (event) => {
      event.preventDefault()
      event.stopImmediatePropagation()
      $modal.modal('hide')
      this.startLogout(link.getAttribute('href') || '/logout')
    })
  }

  startLogout(logoutUrl) {
    this.pendingLogoutUrl = logoutUrl
    window.addEventListener('coachLogoutReady', this.logoutReadyHandler, {
      once: true,
    })
    window.dispatchEvent(
      new CustomEvent('coachLogoutRequested', {
        detail: { logoutUrl },
      }),
    )

    window.setTimeout(() => {
      if (this.pendingLogoutUrl) {
        this.navigateToLogout(this.pendingLogoutUrl)
      }
    }, 2000)
  }

  teardownLogoutHandlers() {
    if (this.logoutLinks) {
      this.logoutLinks.forEach((handler, link) => {
        link.removeEventListener('click', handler, true)
      })
      this.logoutLinks.clear()
    }

    if (this.logoutReadyHandler) {
      window.removeEventListener('coachLogoutReady', this.logoutReadyHandler)
    }
  }

  navigateToLogout(logoutUrl) {
    if (!this.pendingLogoutUrl) {
      return
    }

    this.pendingLogoutUrl = null
    this.restoreHeaderLinks()

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content')
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = logoutUrl

    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'delete'
    form.appendChild(methodInput)

    document.body.appendChild(form)
    form.submit()
  }

  updateHeaderLinksForAvailability() {
    // Get availability from URL parameter
    const urlParams = new URLSearchParams(window.location.search)
    const availability = urlParams.get('availability')

    // Handle availability change
    this.handleAvailabilityChange(availability)
  }

  handleAvailabilityChange(availability) {
    // If coach is available (text_only or audio_video), set header links to open in new tab
    if (availability === 'text_only' || availability === 'audio_video') {
      this.setHeaderLinksToNewTab()
    } else {
      // If coach is away, restore original link targets
      this.restoreHeaderLinks()
    }
  }

  setHeaderLinksToNewTab() {
    // Find all <a> tags in body > .header
    const header = document.querySelector('body > .header')
    if (!header) {
      return
    }

    const links = header.querySelectorAll('a')
    // Store original target values for cleanup (only on first modification)
    if (!this.originalLinkTargets) {
      this.originalLinkTargets = new Map()
    }

    links.forEach((link) => {
      if (this.isLogoutLink(link)) {
        return
      }

      // Store original target only if we haven't stored it before
      if (!this.originalLinkTargets.has(link)) {
        // Store original target (or null if not set)
        this.originalLinkTargets.set(link, link.target || null)
      }
      // Set to _blank regardless of current value (in case it was restored)
      link.target = '_blank'
    })
  }

  restoreHeaderLinks() {
    // Restore original target values (but keep the map for future use)
    if (this.originalLinkTargets) {
      this.originalLinkTargets.forEach((originalTarget, link) => {
        // Only restore if link still exists in DOM
        if (link.isConnected) {
          if (this.isLogoutLink(link)) {
            link.removeAttribute('target')
            return
          }

          if (originalTarget === null) {
            link.removeAttribute('target')
          } else {
            link.target = originalTarget
          }
        } else {
          // Remove from map if link no longer exists
          this.originalLinkTargets.delete(link)
        }
      })
    }
  }

  async waitForEasyRTC() {
    console.log('Waiting for openEasyRtc')
    // Wait for openEasyRtc to be loaded and available on window
    // I only saw it wait once, this is more about bridging `require` and npm dependencies
    while (!window.openEasyRtc) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    console.log('openEasyRtc is available')
    // Now initialize the React component
    this.initializeReactComponent()
  }

  initializeReactComponent() {
    // Create React root and render the component
    this.reactRoot = createRoot(this.containerTarget)

    const props = {
      // Socket and room configuration
      socketUrl: this.socketUrlValue,
      username: this.usernameValue,
      chatRoom: this.chatRoomValue,
      log_chat_event_path: this.logChatEventPathValue,
      serverTime: this.serverTimeValue,
      current_user: { locale: this.currentUserLocaleValue },

      // Coach-specific information
      userDisplayName: this.userDisplayNameValue,
      userFirstName: this.userFirstNameValue,
      userAbbreviatedName: this.userAbbreviatedNameValue,
      avatar: this.avatarValue,
      bigAvatar: this.bigAvatarValue,
      isTechSupport: this.isTechSupportValue,
      browserName: this.browserNameValue,
      os: this.osValue,
      coachLocales: this.coachLocalesValue,

      // Chat configuration and paths
      coachChatPath: this.coachChatPathValue,
      logChatMissedOpportunityPath: this.logChatMissedOpportunityPathValue,
      checkCoachHeartbeatPath: this.checkCoachHeartbeatPathValue,
      coach_chat_candidate_info_path: this.coachChatCandidateInfoPathValue,
      coach_end_chat_path: this.endChatPathValue,
      autoconnect: this.autoconnectValue,
      userId: this.userIdValue,

      // Availability configuration
      initialAvailability: this.initialAvailabilityValue,

      // Audio settings URLs as arrays
      ringToneOptions: this.ringToneOptionsValue || [],
      messageSoundOptions: this.messageSoundOptionsValue || [],
    }

    this.reactRoot.render(React.createElement(CoachChat, props))
  }
}
